/**
 * Desk Continuum — pair localhost desk-bridge, plan with CLAW, execute under sandbox.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LinkIcon from '@mui/icons-material/Link';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { adviseDemoAssistantApi, getDeskCatalogApi } from '../../api/demoAssistants.api';
import ClawDecisionPanel from '../../components/demo/ClawDecisionPanel';
import DeskOrbitSky from '../../components/demo/DeskOrbitSky';
import {
  DEFAULT_BRIDGE_BASE,
  clearDeskBridgePairing,
  deskBridgeHealth,
  deskBridgeRunTool,
  deskBridgeSetDryRun,
  deskBridgeStatus,
  loadDeskBridgePairing,
  saveDeskBridgePairing,
} from '../../utils/deskBridgeClient';

const INK = '#0f172a';
const TEAL = '#0f766e';
const ACCENT = '#c2410c';

const PHASES = [
  { id: 'orbit', label: 'Dawn Orbit', hint: 'Local + tech + sacred + NASA sky' },
  { id: 'arrive', label: 'Arrive', hint: 'Sense calendar, inbox, sticky' },
  { id: 'triage', label: 'Triage', hint: 'Classify downloads-staging' },
  { id: 'focus', label: 'Focus', hint: 'Write focus.md' },
  { id: 'meeting', label: 'Meeting pack', hint: 'Gather matching files' },
  { id: 'ship', label: 'Ship', hint: 'git status on demo-app' },
  { id: 'close', label: 'Close', hint: 'Journal + archive' },
];

function downloadPacket(packet) {
  const blob = new Blob([JSON.stringify(packet, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `desk-continuum-${packet?.intent || 'plan'}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function DeskContinuumPortalPage() {
  const pairing0 = loadDeskBridgePairing();
  const [baseUrl, setBaseUrl] = useState(pairing0.baseUrl || DEFAULT_BRIDGE_BASE);
  const [token, setToken] = useState(pairing0.token || '');
  const [bridgeHealth, setBridgeHealth] = useState(null);
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [pairError, setPairError] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [intent, setIntent] = useState('orbit');
  const [topic, setTopic] = useState('Dawn Orbit');
  const [context, setContext] = useState('');
  const [scenarioId, setScenarioId] = useState('D0');
  const [profileId, setProfileId] = useState('muslim-bay-area');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);
  const [execResult, setExecResult] = useState(null);
  const [trail, setTrail] = useState([]);

  const paired = Boolean(bridgeStatus?.success);

  useEffect(() => {
    getDeskCatalogApi().then((d) => setCatalog(d?.catalog || d)).catch(() => {});
  }, []);

  useEffect(() => {
    const sc = (catalog?.scenarios || []).find((s) => s.id === scenarioId);
    if (!sc) return;
    setIntent(sc.intent || 'orbit');
    setTopic(sc.topic || '');
    setContext(sc.context || '');
    if (sc.profileId) setProfileId(sc.profileId);
  }, [scenarioId, catalog]);

  const refreshHealth = useCallback(async () => {
    try {
      const h = await deskBridgeHealth(baseUrl);
      setBridgeHealth(h);
      setPairError(null);
      return h;
    } catch (e) {
      setBridgeHealth(null);
      setPairError(e.message || 'Bridge not reachable on localhost');
      return null;
    }
  }, [baseUrl]);

  useEffect(() => {
    refreshHealth();
    const t = setInterval(refreshHealth, 8000);
    return () => clearInterval(t);
  }, [refreshHealth]);

  const onPair = async () => {
    setBusy('pair');
    setPairError(null);
    try {
      await refreshHealth();
      saveDeskBridgePairing({ baseUrl, token });
      const st = await deskBridgeStatus(baseUrl, token);
      setBridgeStatus(st);
      setDryRun(st.dryRun !== false);
      setTrail((prev) => [...prev, { tool: 'if_desk_status', at: new Date().toISOString(), ok: true }]);
    } catch (e) {
      setBridgeStatus(null);
      setPairError(e.message || 'Pair failed — check token');
    } finally {
      setBusy(null);
    }
  };

  const onToggleDryRun = async (next) => {
    setDryRun(next);
    if (!paired) return;
    try {
      await deskBridgeSetDryRun(baseUrl, token, next);
      const st = await deskBridgeStatus(baseUrl, token);
      setBridgeStatus(st);
    } catch (e) {
      setError(e.message);
    }
  };

  const onPlan = async () => {
    setBusy('plan');
    setError(null);
    setExecResult(null);
    try {
      const out = await adviseDemoAssistantApi('desk-agent', {
        intent,
        topic,
        context,
        profileId,
        scenarioId: scenarioId || undefined,
        bridgeOnline: paired,
        dryRun,
      });
      setPlan(out);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Plan failed');
    } finally {
      setBusy(null);
    }
  };

  const bridgePlan = plan?.result?.bridgePlan || plan?.bridgePlan;
  const result = plan?.result || plan;

  const runBridgeTool = async (tool, args = {}, label = tool) => {
    if (!paired) {
      setError('Pair the desk-bridge before executing');
      return null;
    }
    setBusy(label);
    setError(null);
    try {
      const data = await deskBridgeRunTool(baseUrl, token, tool, args);
      setExecResult(data);
      setTrail((prev) => [...prev, {
        tool,
        at: new Date().toISOString(),
        ok: data.success !== false,
        summary: data.summary?.content || data.summary || data.message,
      }]);
      return data;
    } catch (e) {
      setError(e.message || 'Bridge tool failed');
      setTrail((prev) => [...prev, { tool, at: new Date().toISOString(), ok: false, summary: e.message }]);
      return null;
    } finally {
      setBusy(null);
    }
  };

  const onExecute = async () => {
    const tool = bridgePlan?.tool || {
      orbit: 'if_desk_orbit',
      arrive: 'if_desk_sense_day',
      triage: 'if_desk_triage_downloads',
      focus: 'if_desk_focus_block',
      meeting: 'if_desk_meeting_pack',
      ship: 'if_desk_ship_check',
      close: 'if_desk_close_day',
    }[intent];
    const args = {
      ...(bridgePlan?.arguments || {}),
      topic: topic || bridgePlan?.arguments?.topic,
      profileId: intent === 'orbit' ? profileId : undefined,
      project: intent === 'ship' ? (topic || 'demo-app') : undefined,
      note: intent === 'close' ? context : undefined,
      apply: false,
    };
    await runBridgeTool(tool, args, 'execute');
  };

  const onConfirmApply = async () => {
    const planType = bridgePlan?.applyPlanType || intent;
    await runBridgeTool('if_desk_apply_plan', {
      planType: planType === 'arrive' ? 'triage' : planType,
      topic,
      note: context,
      profileId,
      project: topic || 'demo-app',
    }, 'apply');
  };

  const needsApply = useMemo(() => {
    if (!execResult || execResult.dryRun === false) return false;
    return ['orbit', 'triage', 'focus', 'meeting', 'close'].includes(intent) && execResult.dryRun === true;
  }, [execResult, intent]);

  const orbitView = execResult?.sky || execResult?.thread ? execResult : null;

  const checklistRows = execResult?.checklist?.rows
    || result?.checklist?.rows
    || [];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f1f5f4',
        background: 'radial-gradient(1200px 600px at 10% -10%, rgba(15,118,110,0.14), transparent), linear-gradient(180deg, #ecfdf8 0%, #f8fafc 45%)',
        color: INK,
      }}
      data-testid="desk-continuum-portal"
    >
      <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 } }}>
        <Stack spacing={0.5} sx={{ mb: 2.5 }}>
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Fraunces", "Iowan Old Style", Georgia, serif',
              fontWeight: 700,
              fontSize: { xs: '1.85rem', sm: '2.35rem' },
              letterSpacing: '-0.02em',
              color: TEAL,
            }}
          >
            Desk Continuum
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 620 }}>
            Start with
            {' '}
            <strong>Dawn Orbit</strong>
            {' '}
            — local satellites, tech comets, sacred rhythm, and NASA sky —
            then Triage → Focus → Ship → Close. Bridge acts only inside
            {' '}
            <code>~/InterviewFlow-Desk-Sandbox</code>
            .
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label="CLAW if-desk-agent" sx={{ fontFamily: 'monospace' }} />
            <Chip
              size="small"
              color={bridgeHealth?.ok ? 'success' : 'default'}
              label={bridgeHealth?.ok ? `bridge :${String(baseUrl).split(':').pop()}` : 'bridge offline'}
              data-testid="desk-bridge-health"
            />
            {paired && (
              <Chip size="small" color="success" icon={<CheckCircleOutlineIcon />} label="paired" />
            )}
            <Link component={RouterLink} to="/studio/help/desk-agent" underline="hover" variant="caption">
              Help
            </Link>
            <Link component={RouterLink} to="/v2/demo/personal" underline="hover" variant="caption">
              Personal assistant (drafts)
            </Link>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr' },
            gap: 2,
          }}
        >
          <Stack spacing={2}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: '#fff',
                border: '1px solid rgba(15,118,110,0.18)',
              }}
              data-testid="desk-pair-card"
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                1 · Pair desk-bridge
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Run
                {' '}
                <code>npm run desk-bridge</code>
                {' '}
                in the repo, then paste the token printed in the terminal.
              </Typography>
              <Stack spacing={1.25}>
                <TextField
                  size="small"
                  label="Bridge URL"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  fullWidth
                  type="password"
                  autoComplete="off"
                />
                {pairError && <Alert severity="warning">{pairError}</Alert>}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    startIcon={busy === 'pair' ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
                    onClick={onPair}
                    disabled={Boolean(busy) || !token.trim()}
                    sx={{ textTransform: 'none', bgcolor: TEAL, '&:hover': { bgcolor: TEAL, filter: 'brightness(0.92)' } }}
                    data-testid="desk-pair-btn"
                  >
                    Pair
                  </Button>
                  <Button
                    size="small"
                    onClick={() => { clearDeskBridgePairing(); setToken(''); setBridgeStatus(null); }}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear
                  </Button>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={dryRun}
                        onChange={(e) => onToggleDryRun(e.target.checked)}
                        size="small"
                      />
                    )}
                    label="Dry-run"
                  />
                </Stack>
                {bridgeStatus?.sandboxRoot && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {bridgeStatus.sandboxRoot}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: '#fff',
                border: '1px solid rgba(15,118,110,0.18)',
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                2 · Intent
              </Typography>
              <Stack spacing={1.25}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="desk-intent-label">Phase</InputLabel>
                  <Select
                    labelId="desk-intent-label"
                    label="Phase"
                    value={intent}
                    onChange={(e) => setIntent(e.target.value)}
                    data-testid="desk-intent"
                  >
                    {PHASES.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.label} — {p.hint}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(catalog?.scenarios || []).length > 0 && (
                  <FormControl size="small" fullWidth>
                    <InputLabel id="desk-scenario-label">Runbook</InputLabel>
                    <Select
                      labelId="desk-scenario-label"
                      label="Runbook"
                      value={scenarioId}
                      onChange={(e) => setScenarioId(e.target.value)}
                    >
                      <MenuItem value="">Custom</MenuItem>
                      {catalog.scenarios.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.id} · {s.topic}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {(intent === 'orbit' || (catalog?.profiles || []).length > 0) && (
                  <FormControl size="small" fullWidth>
                    <InputLabel id="desk-profile-label">Desk profile</InputLabel>
                    <Select
                      labelId="desk-profile-label"
                      label="Desk profile"
                      value={profileId}
                      onChange={(e) => setProfileId(e.target.value)}
                      data-testid="desk-profile"
                    >
                      {(catalog?.profiles || [
                        { id: 'muslim-bay-area', label: 'Muslim · Bay Area' },
                        { id: 'christian-lectionary', label: 'Christian · Lectionary' },
                        { id: 'jewish-shabbat', label: 'Jewish · Shabbat-aware' },
                        { id: 'secular-maker', label: 'Secular · Maker' },
                      ]).map((p) => (
                        <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  size="small"
                  label="Topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Notes"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Button
                  variant="contained"
                  onClick={onPlan}
                  disabled={Boolean(busy)}
                  startIcon={busy === 'plan' ? <CircularProgress size={14} color="inherit" /> : null}
                  sx={{ textTransform: 'none', bgcolor: TEAL, alignSelf: 'flex-start' }}
                  data-testid="desk-plan-btn"
                >
                  Plan day
                </Button>
              </Stack>
            </Box>
          </Stack>

          <Stack spacing={2}>
            {orbitView && <DeskOrbitSky orbit={orbitView} />}

            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: '#fff',
                border: '1px solid rgba(15,118,110,0.18)',
                minHeight: 280,
              }}
              data-testid="desk-outcome"
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" fontWeight={700}>3 · Plan / Act</Typography>
                {result?.routing?.primaryTrackLabel && (
                  <Chip size="small" label={result.routing.primaryTrackLabel} sx={{ bgcolor: TEAL, color: '#fff' }} />
                )}
              </Stack>

              {!orbitView && (result?.summary?.content || execResult?.summary?.content) && (
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'ui-sans-serif, system-ui',
                    mb: 1.5,
                    lineHeight: 1.45,
                  }}
                >
                  {execResult?.summary?.content || result?.summary?.content}
                </Typography>
              )}
              {orbitView && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Constellation above. Confirm apply to pin
                  {' '}
                  <code>orbit/{orbitView.day || 'today'}.md</code>
                  {' '}
                  in your sandbox.
                </Typography>
              )}

              {checklistRows.length > 0 && (
                <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                  {checklistRows.slice(0, 10).map((row) => (
                    <Typography key={row.id || row.label} variant="caption" display="block">
                      •
                      {' '}
                      {row.label}
                      {row.detail ? ` — ${row.detail}` : ''}
                      {row.status ? ` [${row.status}]` : ''}
                    </Typography>
                  ))}
                </Stack>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  startIcon={busy === 'execute' ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                  onClick={onExecute}
                  disabled={!plan || Boolean(busy) || !paired}
                  sx={{ textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT, filter: 'brightness(0.95)' } }}
                  data-testid="desk-execute-btn"
                >
                  Run on machine
                </Button>
                {needsApply && (
                  <Button
                    variant="outlined"
                    onClick={onConfirmApply}
                    disabled={Boolean(busy)}
                    sx={{ textTransform: 'none', borderColor: TEAL, color: TEAL }}
                    data-testid="desk-apply-btn"
                  >
                    Confirm apply
                  </Button>
                )}
                {(plan || execResult) && (
                  <Button
                    size="small"
                    startIcon={<DownloadOutlinedIcon />}
                    onClick={() => downloadPacket({
                      intent,
                      topic,
                      plan: result,
                      execResult,
                      trail,
                      sandboxRoot: bridgeStatus?.sandboxRoot,
                    })}
                    sx={{ textTransform: 'none' }}
                  >
                    Export packet
                  </Button>
                )}
              </Stack>

              {result?.claw && (
                <Box sx={{ mt: 1.5 }}>
                  <ClawDecisionPanel
                    claw={result.claw}
                    handoffPacket={result.handoffPacket}
                    accent={TEAL}
                    defaultOpen={false}
                    audience="citizen"
                  />
                </Box>
              )}
            </Box>

            {trail.length > 0 && (
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(15,118,110,0.06)' }} data-testid="desk-claw-trail">
                <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                  CLAW trail
                </Typography>
                {trail.slice(-8).map((t, i) => (
                  <Typography key={`${t.tool}-${i}`} variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                    {t.ok ? '✓' : '✗'}
                    {' '}
                    {t.tool}
                  </Typography>
                ))}
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
